import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getMakesKey, getMakes, getModelsKey, getModels } from '../../../../api-client/search'
import useDebouncedCallback from '../../../../hooks/use-debounced-callback'
import Magnifier from '../../../../components/svg/magnifier'
import { FormField } from '../../../../components/inputs'
import ListItemCheckbox from './list-item-checkbox'
import { Button } from '../../../../components/button'
import { Mobile } from '../../../../components/media'

export default function MakeModelFilter({
  checkedMakes,
  onMakeChange,
  checkedModels,
  onModelChange,
  onAllModelSet,
  handleFilterToggle,
}) {
  const [makeSearchWord, setMakeSearchWord] = useState('')
  const [currentMake, setCurrentMake] = useState(null)

  const { data: allMakes = [] } = useQuery({
    queryKey: getMakesKey,
    queryFn: getMakes,
  })

  const { data: models = [] } = useQuery({
    queryKey: getModelsKey + currentMake?.id,
    queryFn: () => getModels({ makeId: currentMake.id }),
    enabled: !!currentMake,
  })

  const filteredMakes = useMemo(() => {
    if (!makeSearchWord) return allMakes

    return allMakes.filter(
      ({ name }) => name != null && name.toLowerCase().includes(makeSearchWord.toLowerCase()),
    )
  }, [makeSearchWord, allMakes])

  const handleSearchWordChange = useDebouncedCallback((value) => setMakeSearchWord(value), 500, [])

  const handleMakeChange = useCallback(
    (make, checked) => {
      setCurrentMake(make)
      onMakeChange(make, checked)
    },
    [onMakeChange],
  )

  const handleModelChange = useCallback(
    (model, checked) => {
      onModelChange(model, checked)
    },
    [onModelChange],
  )

  return (
    <div className='wrapper'>
      {!currentMake && (
        <div className='makesView'>
          <label htmlFor='makeSearchField'>Search make</label>
          <FormField
            id='makesSearchField'
            onChange={(e) => handleSearchWordChange(e.target.value)}
            adornment={<Magnifier fill='var(--secondary-text-color)' width='17' />}
          />
          <ul className='makesList'>
            {filteredMakes.map((make) => (
              <ListItemCheckbox
                key={make.id}
                value={make}
                checked={!!checkedMakes[make.id]}
                onChange={handleMakeChange}
              >
                {make.name}
              </ListItemCheckbox>
            ))}
          </ul>
        </div>
      )}

      {!!currentMake && (
        <div className='modelsView'>
          <button className='flatBtn backBtn' onClick={() => setCurrentMake(null)}>
            &lt; Back to Makes
          </button>
          <div className='currentMakeName'>{currentMake.name}</div>
          <div className='description'>
            Select a model or
            <button className='flatBtn' onClick={() => onAllModelSet(models)}>
              all models
            </button>
          </div>
          <div className='modelsContainer'>
            <ul className='modelsList flex flexWrap justifyBetween'>
              {models.map((model) => (
                <ListItemCheckbox
                  key={model.id + model.name}
                  value={model}
                  checked={!!checkedModels[model.name.toLowerCase()]}
                  onChange={handleModelChange}
                >
                  {model.name}
                </ListItemCheckbox>
              ))}
            </ul>
          </div>
        </div>
      )}
      <Mobile>
        <div className='applyButton'>
          <Button onClick={() => handleFilterToggle('makeModel')}>Apply</Button>
        </div>
      </Mobile>

      <style jsx>{`
        .applyButton {
          position: fixed;
          left: calc(50% - 46.9px);
          top: 18rem;
        }
        .wrapper {
          min-height: 29.8rem;
          padding: 2rem;
          border-radius: 1rem;
          background: #fff;
          box-shadow: 0 0.3rem 0.6rem rgba(0, 0, 0, 0.16);
          color: var(--secondary-text-color);
        }

        .makesView {
          > label {
            display: block;
            margin: 0 0 1.1rem;
            font-size: 1.6rem;
            font-weight: 700;
          }

          .makesList {
            margin: 6rem 0 0;
            height: 20.6rem;
            overflow: auto;

            :global(button:active) {
              outline: none;
            }
          }
        }

        .modelsView {
          .backBtn {
            font-size: 1.6rem;
            font-weight: 700;
          }

          .currentMakeName {
            margin: 1.8rem 0 0.4rem;
            text-align: center;
            color: var(--primary-text-color);
            font-size: 2.4rem;
            font-weight: 700;
          }

          .description {
            margin: 0 0 1.6rem;
            text-align: center;
            font-size: 1.6rem;
            font-weight: 700;

            button {
              margin: 0 0 0 0.5rem;
              color: var(--text-link-color);
              font: inherit;
            }
          }

          .modelsContainer {
            height: 26.5rem;
            overflow: auto;
            margin-top: 6rem;
          }

          .modelsList {
            width: 100%;

            :global(li) {
              max-width: 18.8rem;
              width: 100%;
            }
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            max-width: 100%;
            padding: 3rem;
            box-shadow: none;
            border-radius: 0;
          }

          .makesList {
            height: 100% !important;
          }

          .makesView {
            > label {
              font-size: 1.2rem;
            }
          }

          .modelsView {
            display: block;

            .modelsContainer {
              height: 100%;
            }

            .backBtn {
              font-size: 1.2rem;
            }

            .currentMakeName {
              font-size: 1.8rem;
            }

            .description {
              font-size: 1.4rem;
            }

            .modelsList {
              :global(li) {
                max-width: 100%;
              }
            }
          }
        }
      `}</style>
    </div>
  )
}
